import type { ContradictionClass, ReactorEffect } from '../types';

export interface ReactorDef {
  id: string;
  name: { en: string; pt: string };
  flavor: { en: string; pt: string };
  class: ContradictionClass;
  effect: ReactorEffect;
  baseCoin: number;
  baseClassCost: number;
  costMul: number;
  maxLevel: number;
  /** lifetime Φ required to surface this reactor */
  unlockPhi: number;
  glyph: string;
}

export const REACTORS: ReactorDef[] = [
  {
    id: 'atlas',
    name: { en: 'Atlas Reactor', pt: 'Reator Atlas' },
    flavor: {
      en: 'Anchors what should not balance. Conservation, restored.',
      pt: 'Ancora o que não deveria fechar. Conservação, restaurada.',
    },
    class: 'conservation',
    effect: { kind: 'phi_yield_class', target: 'conservation', per: 0.05 },
    baseCoin: 200,
    baseClassCost: 60,
    costMul: 2.0,
    maxLevel: 20,
    unlockPhi: 0,
    glyph: '⊕',
  },
  {
    id: 'mirror',
    name: { en: 'Mirror Reactor', pt: 'Reator Espelho' },
    flavor: {
      en: 'Refines identity violations into work.',
      pt: 'Refina violações de identidade em trabalho.',
    },
    class: 'identity',
    effect: { kind: 'phi_yield_class', target: 'identity', per: 0.08 },
    baseCoin: 300,
    baseClassCost: 50,
    costMul: 2.2,
    maxLevel: 15,
    unlockPhi: 50,
    glyph: '⌬',
  },
  {
    id: 'loop',
    name: { en: 'Loop Reactor', pt: 'Reator Laço' },
    flavor: {
      en: 'Curls causality. Craft cycles tighten.',
      pt: 'Enrola a causalidade. Ciclos de ofício apertam.',
    },
    class: 'causal',
    effect: { kind: 'craft_duration', per: -0.03 },
    baseCoin: 500,
    baseClassCost: 80,
    costMul: 2.5,
    maxLevel: 12,
    unlockPhi: 100,
    glyph: '↺',
  },
  {
    id: 'sign',
    name: { en: 'Sign Reactor', pt: 'Reator Signo' },
    flavor: {
      en: 'Detaches labels. Market amplitudes swell.',
      pt: 'Solta os rótulos. Amplitudes de mercado aumentam.',
    },
    class: 'semantic',
    effect: { kind: 'market_amplitude', per: 0.025 },
    baseCoin: 800,
    baseClassCost: 70,
    costMul: 2.3,
    maxLevel: 12,
    unlockPhi: 150,
    glyph: '⌐',
  },
  {
    id: 'observer',
    name: { en: 'Observer Reactor', pt: 'Reator Observador' },
    flavor: {
      en: 'Watches the others. Their outputs swell with attention.',
      pt: 'Observa os outros. As saídas crescem com atenção.',
    },
    class: 'reflexive',
    effect: { kind: 'reactor_multiplier', per: 0.1 },
    baseCoin: 2000,
    baseClassCost: 40,
    costMul: 3.0,
    maxLevel: 8,
    unlockPhi: 300,
    glyph: '◉',
  },
];

export function reactorById(id: string): ReactorDef | undefined {
  return REACTORS.find((r) => r.id === id);
}

export function reactorCost(def: ReactorDef, currentLevel: number) {
  return {
    coin: Math.ceil(def.baseCoin * Math.pow(def.costMul, currentLevel)),
    classPhi: Math.ceil(def.baseClassCost * Math.pow(def.costMul, currentLevel)),
  };
}
